import { AutoFixHigh } from '@mui/icons-material';
import { Box, List } from '@mui/material';
import { useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { InfoDialog } from '~/components/dialogs/information';
import { useAppContext } from '~/contexts';
import { StudioProvider } from '../context';
import { useDevicePreview } from '../edit/lib/devicePreview';
import { loadShowRulers, saveShowRulers } from '../edit/lib/storage';
import { Drawer, ListItem, ListItemButton, ListItemIcon, ListItemText } from './styles';
import { linkTranslationKeys, links, reducer, type LinkName } from './utils';

export const StudioLayout = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const { designId } = useParams<{ designId: string }>();
  const [
    {
      activeSettingsTab,
      activeSidebarItem,
      copiedItems,
      dataItemCellSelectOpen,
      dirty,
      editingId,
      fonts,
      history,
      historyStep,
      items,
      loaded,
      message,
      selectedIds,
      showRulers,
      stage,
      transformer,
      zoom,
    },
    dispatch,
  ] = useReducer(reducer, {
    activeSettingsTab: 'Settings',
    activeSidebarItem: 'Text',
    copiedItems: [],
    dataItemCellSelectOpen: false,
    dirty: false,
    history: [[]],
    historyStep: 0,
    items: [],
    loaded: false,
    selectedIds: new Set<string>(),
    showRulers: designId ? loadShowRulers(designId) : true,
    transformer: { dragging: false, transforming: false },
    zoom: 1,
  });

  // Deliberately outside the reducer: previewing a device is a lens over the canvas, not an
  // edit, so it must never enter the undo history or be saved.
  const [previewDeviceId, setPreviewDeviceId] = useState('');
  const devicePreview = useDevicePreview(items, previewDeviceId);

  const displayLinks = useMemo(() => {
    if (selectedIds.size) return links;
    return links.filter((x) => x.name !== 'Settings');
  }, [selectedIds.size]);

  const translateTab = (name: LinkName) => {
    const key = linkTranslationKeys[name];
    return t(`pages.studio.edit.sidebar.tabs.${key}`);
  };

  if (!currentNetwork.canAccessStudio.value)
    return <Navigate replace to={`/networks/${currentNetwork.id}/not-found`} />;

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        position: 'absolute',
        top: '0px',
        left: '0px',
        width: '100%',
      }}
    >
      <nav style={{ width: '98px' }}>
        <Drawer
          variant="permanent"
          anchor="left"
          sx={{
            '.MuiDrawer-paper': { width: '98px' },
          }}
        >
          <List>
            <ListItem sx={{ mb: '20px' }}>
              <ListItemIcon sx={{ color: 'common.white' }}>
                <AutoFixHigh fontSize="large" />
              </ListItemIcon>
            </ListItem>
            {displayLinks.map(({ name, Icon }) => (
              <ListItem key={name}>
                <ListItemButton
                  onClick={() => dispatch({ type: 'SET_SIDEBAR_ITEM', name })}
                  selected={name === activeSidebarItem}
                >
                  <ListItemIcon>
                    <Icon />
                  </ListItemIcon>
                  <ListItemText sx={{ m: 0 }}>{translateTab(name)}</ListItemText>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Drawer>
      </nav>

      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          overflow: 'hidden',
        }}
      >
        <StudioProvider
          activeSettingsTab={activeSettingsTab}
          activeSidebarItem={activeSidebarItem}
          addItems={(items) => dispatch({ type: 'ADD_ITEMS', items })}
          alignItem={(id, position) => dispatch({ type: 'ALIGN_ITEM', id, position })}
          canRedo={historyStep < history.length - 1}
          canUndo={historyStep > 0}
          copiedItems={copiedItems}
          copyItems={(ids) => dispatch({ type: 'COPY_ITEMS', ids })}
          dataItemCellSelectOpen={dataItemCellSelectOpen}
          deleteItems={(ids) => dispatch({ type: 'DELETE_ITEMS', ids })}
          dirty={dirty}
          duplicateItems={(items) => {
            dispatch({
              type: 'ADD_ITEMS',
              items: items.map((item) => ({
                ...item,
                id: uuid(),
                x: item.x + 20,
                y: item.y + 20,
              })),
            });
          }}
          editItem={(id) => dispatch({ type: 'EDIT_ITEM', id })}
          editingItem={items.find((x) => x.id === editingId)}
          fonts={fonts}
          items={items}
          loadItems={(items) => dispatch({ type: 'LOAD_ITEMS', items })}
          loaded={loaded}
          moveItem={(id, direction) => dispatch({ type: 'MOVE_ITEM', id, direction })}
          redo={() => dispatch({ type: 'REDO' })}
          resetDirty={() => dispatch({ type: 'RESET_DIRTY' })}
          toggleLockItems={(ids) => dispatch({ type: 'TOGGLE_LOCK_ITEMS', ids })}
          reorderItems={(ids) => dispatch({ type: 'REORDER_ITEMS', ids })}
          selectItems={(ids) => dispatch({ type: 'SELECT_ITEMS', ids })}
          selectedIds={selectedIds}
          selectedItems={items.filter((item) => selectedIds.has(item.id))}
          setActiveSettingsTab={(tab) => dispatch({ type: 'SET_SETTINGS_TAB', tab })}
          setDataItemCellSelectOpen={(open) =>
            dispatch({ type: 'SET_DATA_ITEM_CELL_SELECT', open })
          }
          setFonts={(fonts) => dispatch({ type: 'SET_FONTS', fonts })}
          previewDeviceId={previewDeviceId}
          setPreviewDeviceId={setPreviewDeviceId}
          previewTextFor={devicePreview.textFor}
          setMessage={(message) => dispatch({ type: 'SET_MESSAGE', message })}
          setShowRulers={(show) => {
            dispatch({ type: 'SET_SHOW_RULERS', show });
            if (designId) saveShowRulers(designId, show);
          }}
          setSidebarItem={(name) => dispatch({ type: 'SET_SIDEBAR_ITEM', name })}
          showRulers={showRulers}
          setStage={(stage) => dispatch({ type: 'SET_STAGE', stage })}
          setTransformer={(transformer) => dispatch({ type: 'SET_TRANSFORMER', transformer })}
          setZoom={(zoom) => dispatch({ type: 'SET_ZOOM', zoom })}
          stage={stage}
          transformer={transformer}
          undo={() => dispatch({ type: 'UNDO' })}
          zoom={zoom}
          updateItems={(items) => dispatch({ type: 'UPDATE_ITEMS', items })}
        >
          <Outlet />
          {message && (
            <InfoDialog
              onDismiss={() => dispatch({ type: 'SET_MESSAGE', message: undefined })}
              open
              message={message}
              title={t('pages.studio.max_videos_title')}
            />
          )}
        </StudioProvider>
      </Box>
    </Box>
  );
};
